import type { LinkOptions } from "@tanstack/react-router";
import { Link as RouterLink } from "@tanstack/react-router";
import React from "react";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

const link = tv({
  base: `hover:underline`,
  variants: {
    color: {
      primary: "text-primary",
      secondary: "text-text-primary",
      tertiary: "text-text-tertiary",
    },
    weight: {
      medium: "font-medium",
      normal: "",
    },
    size: {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
    },
  },
  defaultVariants: {
    color: "primary",
    size: "md",
    weight: "medium",
  },
});

// href is omitted on purpose (from the anchor props AND from LinkOptions,
// which also declares it): the router builds the anchor's href from `to` and
// silently discards a passed href, leaving a link that resolves to "/";
// use `to`/`params`, or ExternalLink for urls the router cannot express
export type LinkProps = Omit<React.ComponentProps<"a">, "href"> &
  VariantProps<typeof link> &
  Omit<LinkOptions, "href">;

export default function Link({
  size,
  color,
  weight,
  className,
  ...props
}: LinkProps) {
  return (
    <RouterLink
      className={link({ size, color, className, weight })}
      {...props}
    />
  );
}

export type ExternalLinkProps = React.ComponentProps<"a"> &
  VariantProps<typeof link>;

/** same styling as Link, but a native anchor for urls the router cannot
 * express (backend file endpoints, third-party pages) */
export function ExternalLink({
  size,
  color,
  weight,
  className,
  children,
  ...props
}: ExternalLinkProps) {
  return (
    <a className={link({ size, color, className, weight })} {...props}>
      {children}
    </a>
  );
}
